import { EventEmitter } from './event-emitter';
import { AudioPlayer } from './audio';
import { SpritePlayer } from './sprite-player';
import { ActionPrompt } from './action-prompt';

export class WaterFaucetScene extends EventEmitter {
	private static instance: WaterFaucetScene;

	public sceneElements = {
		actionHelper: document.getElementById('water-action-helper') as HTMLElement,
		bathroomSink: document.querySelector('#bathroom-sink') as any as HTMLImageElement,
		bathroomSinkContainer: document.querySelector('.bathroom-sink-container') as HTMLElement,
		bathroomBackground: document.querySelector('#bathroom-background') as any as HTMLImageElement,
		coffeeMug: {
			container: document.querySelector('.coffee-mug-container') as HTMLElement,
			mug: document.querySelector('#coffee-mug') as any as HTMLImageElement,
			toothbrush: document.querySelector('#toothbrush') as any as HTMLImageElement,
		},
		fish: {
			primary: {
				container: document.querySelector('.fish-container.primary') as HTMLElement,
				fishTwo: {
					one: document.querySelector('.fish-img.fish-img-two.one') as any as HTMLImageElement,
					two: document.querySelector('.fish-img.fish-img-two.two') as any as HTMLImageElement,
					three: document.querySelector('.fish-img.fish-img-two.three') as any as HTMLImageElement,
				},
				fishThree: {
					one: document.querySelector('.fish-img.fish-img-three.one') as any as HTMLImageElement,
					two: document.querySelector('.fish-img.fish-img-three.two') as any as HTMLImageElement,
				},
				fishFour: document.querySelector('.fish-img.fish-img-four.one') as any as HTMLImageElement,
			},
			secondary: {
				container: document.querySelector('.fish-container.secondary') as any as HTMLImageElement,
				one: document.querySelector('.fish-img.fish-img-one.one') as any as HTMLImageElement,
				two: document.querySelector('.fish-img.fish-img-one.two') as any as HTMLImageElement,
				three: document.querySelector('.fish-img.fish-img-one.three') as any as HTMLImageElement,
				four: document.querySelector('.fish-img.fish-img-one.four') as any as HTMLImageElement,
				five: document.querySelector('.fish-img.fish-img-one.five') as any as HTMLImageElement,
				six: document.querySelector('.fish-img.fish-img-one.six') as any as HTMLImageElement,
			},
		},
		flowerOne: document.querySelector('.first-flower-container') as HTMLElement,
		primaryWaves: {
			container: document.querySelector('.primary-waves') as any as HTMLImageElement,
			one: document.querySelector('.primary-waves .waves-img.one') as any as HTMLImageElement,
			two: document.querySelector('.primary-waves .waves-img.two') as any as HTMLImageElement,
			three: document.querySelector('.primary-waves .waves-img.three') as any as HTMLImageElement,
			four: document.querySelector('.primary-waves .waves-img.four') as any as HTMLImageElement,
			five: document.querySelector('.primary-waves .waves-img.five') as any as HTMLImageElement,
		},
		runningWaterPreload: document.getElementById('running-water-sprite-preload') as any as HTMLImageElement,
		sceneContainer: document.getElementById('water-faucet-scene-container') as HTMLElement,
		secondaryWaves: {
			container: document.querySelector('.secondary-waves') as HTMLElement,
			one: document.querySelector('.secondary-waves .waves-img.one') as any as HTMLImageElement,
			two: document.querySelector('.secondary-waves .waves-img.two') as any as HTMLImageElement,
			three: document.querySelector('.secondary-waves .waves-img.three') as any as HTMLImageElement,
		},
		tapHandle: {
			base: document.querySelector('#sink-tap') as any as HTMLImageElement,
			glow: document.querySelector('#sink-tap-glow-overlay') as any as HTMLImageElement,
		},
		towels: document.querySelector('#towels') as any as HTMLImageElement,
	};

	// Sprites
	public runningWater!: HTMLElement;
	protected runningWatersprite!: SpritePlayer;

	private animationSequenceFinished = false;
	private animationSequenceStarted = false;
	private voiceOverStarted = false;

	private audioPlayer = AudioPlayer.getInstance();
	private actionHelper = ActionPrompt.getInstance();
	private timeouts: any = [];

	public constructor() {
		super();
	}

	public static getInstance(): WaterFaucetScene {
		if (!WaterFaucetScene.instance) {
			WaterFaucetScene.instance = new WaterFaucetScene();
		}

		return WaterFaucetScene.instance;
	}

	public init(): void {
		this.sceneElements.sceneContainer.style.opacity = '1';
		this.audioPlayer.voiceOvers.opening.play();
		this.audioPlayer.soundFx.waterFaucetScene.runningWater.play();

		this.runningWater = document.getElementById('running-water-sprite-container') as HTMLElement;
		this.runningWatersprite = new SpritePlayer({
			spriteElement: this.runningWater,
			spriteFrames: 3,
			animationSpeedInterval: 200,
		});
		this.runningWatersprite.play(true);

		this.listenForTapClick();

		this.audioPlayer.voiceOvers.opening.on('end', () => {
			this.timeouts.push(
				setTimeout(() => {
					this.raiseWater();
				}),
				setTimeout(() => {
					this.voiceOverStarted = true;
					this.audioPlayer.voiceOvers.opening.stop();
					this.audioPlayer.voiceOvers.waterFaucet.play();
				}, 1000)
			);
		});
	}

	private listenForTapClick(): void {
		this.sceneElements.actionHelper.addEventListener('click', () => {
			if (!this.voiceOverStarted) {
				this.audioPlayer.voiceOvers.opening.stop();
				this.audioPlayer.voiceOvers.waterFaucet.play();
			}

			this.sceneElements.tapHandle.glow.classList.remove('pulse');
			this.actionHelper.hideActionPrompt();

			setTimeout(() => {
				this.sceneElements.tapHandle.base.classList.add('off');
				this.runningWatersprite.stop();
				this.runningWatersprite.hide();

				this.drainWater();
			});
		});
	}

	private raiseWater(): void {
		this.sceneElements.primaryWaves.one.classList.add('shown');

		this.timeouts.push(
			setTimeout(() => {
				this.animationSequenceStarted = true;
				this.animateFish();

				this.sceneElements.coffeeMug.mug.classList.add('float');
				this.sceneElements.coffeeMug.toothbrush.classList.add('float');
				this.sceneElements.primaryWaves.two.classList.add('shown');
			}, 1000)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.secondaryWaves.three.classList.add('shown');
				this.sceneElements.primaryWaves.three.classList.add('shown');
			}, 2000)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.audioPlayer.soundFx.waterFaucetScene.runningWater.stop();
				this.audioPlayer.soundFx.waterFaucetScene.waterSplashing.play();
				this.audioPlayer.soundFx.waterFaucetScene.underwaterAmbience.play();

				this.sceneElements.secondaryWaves.one.classList.add('shown');
				this.sceneElements.secondaryWaves.two.classList.add('shown');
			}, 3000)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.audioPlayer.soundFx.general.actionChime.play();
				this.sceneElements.tapHandle.glow.classList.add('pulse');
				this.sceneElements.primaryWaves.four.classList.add('shown');
			}, 3500)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.primaryWaves.five.classList.add('shown');
			}, 4000)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.animationSequenceFinished = true;
			}, 7500)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.actionHelper.showActionPrompt('67%', '77%');
			}, 8000)
		);

		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.actionHelper.click();
			}, 13000)
		);
	}

	private drainWater(): void {
		this.audioPlayer.soundFx.waterFaucetScene.runningWater.stop();
		this.audioPlayer.soundFx.waterFaucetScene.waterSplashing.stop();
		this.audioPlayer.soundFx.waterFaucetScene.underwaterAmbience.stop();
		this.audioPlayer.soundFx.waterFaucetScene.waterDraining.play();
		this.audioPlayer.soundFx.waterFaucetScene.faucetSqueak.play();

		this.timeouts.forEach((timeout: any) => {
			clearTimeout(timeout);
		});

		this.sceneElements.actionHelper.remove();
		this.sceneElements.fish.primary.container.classList.add('drain');
		this.sceneElements.fish.secondary.container.classList.add('drain');
		this.sceneElements.primaryWaves.container.classList.add('drain');
		this.sceneElements.secondaryWaves.container.classList.add('drain');

		if (!this.animationSequenceFinished) {
			this.sceneElements.coffeeMug.container.classList.add('drain');
		} else if (this.animationSequenceFinished) {
			this.sceneElements.coffeeMug.container.remove();
		}

		this.sceneElements.flowerOne.style.display = 'block';

		setTimeout(
			() => {
				this.sceneElements.towels.classList.add('drain');
				this.sceneElements.bathroomSinkContainer.classList.add('drain');
				this.sceneElements.coffeeMug.mug.style.animationPlayState = 'paused';
				this.sceneElements.coffeeMug.toothbrush.style.animationPlayState = 'paused';
				this.eventEmitter.dispatchEvent(new Event('finished'));
			},
			this.animationSequenceStarted ? 1000 : 0
		);

		setTimeout(() => {
			this.sceneElements.bathroomBackground.style.display = 'none';
			this.audioPlayer.soundFx.general.correctActionChime.play();
		}, 1000);
	}

	private animateFish(): void {
		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.fish.secondary.two.classList.add('float-right');
				this.sceneElements.fish.secondary.three.classList.add('float-right');
				this.sceneElements.fish.secondary.four.classList.add('float-right');
				this.sceneElements.fish.primary.fishThree.one.classList.add('float-left');
				this.sceneElements.fish.primary.fishTwo.three.classList.add('float-right');
				this.sceneElements.fish.primary.fishTwo.two.classList.add('float-right');
			}, 2000)
		);
		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.fish.secondary.one.classList.add('float-right');
				this.sceneElements.fish.primary.fishThree.two.classList.add('float-left');
				this.sceneElements.fish.primary.fishFour.classList.add('float-right');
			}, 3000)
		);
		this.timeouts.push(
			setTimeout(() => {
				this.sceneElements.fish.primary.fishTwo.one.classList.add('float-right');
			}, 4500)
		);
	}
}
